/**
 * Shared list pattern constants
 * Used across Word content processing and Slate fragment cleaning
 * Supports both bullet lists (ul) and ordered lists (ol)
 */

// Bullet patterns for regex matching (HTML processing) - MASTER LIST
export const BULLET_PATTERNS_HTML = [
  { regex: /^•\s+/, length: 2, char: '•' }, // Bullet (most common)
  { regex: /^-\s+/, length: 2, char: '-' }, // Hyphen (existing functionality)
  { regex: /^\*\s+/, length: 2, char: '*' }, // Asterisk
  { regex: /^·\s+/, length: 2, char: '·' }, // Middle dot
  { regex: /^o\s+/, length: 2, char: 'o' }, // Letter o
  { regex: /^\+\s+/, length: 2, char: '+' }, // Plus
  { regex: /^>\s+/, length: 2, char: '>' }, // Greater than
  { regex: /^►\s+/, length: 2, char: '►' }, // Right triangle
  { regex: /^→\s+/, length: 2, char: '→' }, // Right arrow
  { regex: /^◦\s+/, length: 2, char: '◦' }, // White bullet
];

// Ordered list patterns for regex matching (HTML processing) - MASTER LIST
export const ORDERED_LIST_PATTERNS_HTML = [
  // Nested numbering patterns (must come first for proper matching)
  {
    regex: /^\d+\.\d+\.\d+\.\s+/,
    length: 'variable',
    type: 'decimal',
    char: '.',
    nested: true,
  }, // 1.1.1.
  {
    regex: /^\d+\.\d+\.\s+/,
    length: 'variable',
    type: 'decimal',
    char: '.',
    nested: true,
  }, // 1.1. 1.2.
  {
    regex: /^\d+\.\d+\)\s+/,
    length: 'variable',
    type: 'decimal',
    char: ')',
    nested: true,
  }, // 1.1) 1.2)

  // Simple numbering patterns
  { regex: /^\d+\.\s+/, length: 'variable', type: 'decimal', char: '.' }, // 1. 2. 3.
  { regex: /^\d+\)\s+/, length: 'variable', type: 'decimal', char: ')' }, // 1) 2) 3)
  { regex: /^\d+\.\s*/, length: 'variable', type: 'decimal', char: '.' }, // Handle cases with no space after period
];

// Handle Word's specific spacing patterns with non-breaking spaces
export const ORDERED_LIST_PATTERNS_WORD_HTML = [
  // Nested patterns with Word spacing
  {
    regex: /^\d+\.\d+\.\s*(&nbsp;|\u00A0|\s)+/,
    length: 'variable',
    type: 'decimal',
    char: '.',
    nested: true,
    description:
      'Word-style nested with non-breaking spaces (1.1.&nbsp;&nbsp;&nbsp;)',
  },
  {
    regex: /^\d+\.\d+\)\s*(&nbsp;|\u00A0|\s)+/,
    length: 'variable',
    type: 'decimal',
    char: ')',
    nested: true,
    description: 'Word-style nested with parenthesis and non-breaking spaces',
  },

  // Simple patterns with Word spacing
  {
    regex: /^\d+\.\s*(&nbsp;|\u00A0|\s)+/,
    length: 'variable',
    type: 'decimal',
    char: '.',
    description: 'Word-style with non-breaking spaces (1.&nbsp;&nbsp;&nbsp;)',
  },
  {
    regex: /^\d+\)\s*(&nbsp;|\u00A0|\s)+/,
    length: 'variable',
    type: 'decimal',
    char: ')',
    description: 'Word-style with parenthesis and non-breaking spaces',
  },
];

// Combined patterns for processing
export const ALL_ORDERED_LIST_PATTERNS = [
  ...ORDERED_LIST_PATTERNS_WORD_HTML, // Word patterns first (more specific)
  ...ORDERED_LIST_PATTERNS_HTML,
];

// Bullet characters for simple array checks - derived from HTML patterns
export const BULLET_CHARS = BULLET_PATTERNS_HTML.map((pattern) => pattern.char);

// Bullet patterns for regex replacement (plain text processing) - derived from HTML patterns
export const BULLET_PATTERNS_PLAIN_TEXT = BULLET_PATTERNS_HTML.map(
  (pattern) => ({
    regex: pattern.regex,
    replacement: pattern.regex,
  }),
);

// Simple regex patterns for Slate text cleaning - derived from HTML patterns
export const BULLET_PATTERNS_SLATE = BULLET_PATTERNS_HTML.map(
  (pattern) => pattern.regex,
);

// Ordered list patterns for regex replacement (plain text processing) - derived from HTML patterns
export const ORDERED_LIST_PATTERNS_PLAIN_TEXT = ALL_ORDERED_LIST_PATTERNS.map(
  (pattern) => ({
    regex: pattern.regex,
    replacement: pattern.regex,
    type: pattern.type,
  }),
);

// Simple regex patterns for Slate text cleaning - derived from ordered list patterns
export const ORDERED_LIST_PATTERNS_SLATE = ALL_ORDERED_LIST_PATTERNS.map(
  (pattern) => pattern.regex,
);
